import { useEffect, useState } from 'react';

const SAMPLE_SIZE = 32;
// Average luminance (0-255) below which a logo is treated as "dark" — i.e.
// likely to disappear against the footer's dark background and worth an
// automatic glow. Picked empirically: a black-on-transparent wordmark lands
// well under this; a white or brightly colored one lands well over it.
const DARK_LUMINANCE_THRESHOLD = 90;

/**
 * True when `src` is a predominantly dark image, sampled client-side via
 * canvas — used to auto-apply `.brand-logo-glow` in the footer (see
 * SiteFooter) so a dark logo stays visible on the dark burgundy background
 * without an admin having to notice and fix it manually. Fails open (returns
 * false, no glow) on any error: a cross-origin image with no CORS headers
 * taints the canvas and throws on `getImageData` rather than sampling wrong,
 * and an untouched logo today already renders exactly as it does before this
 * existed.
 */
export function useIsLogoDark(src: string | null | undefined, enabled: boolean): boolean {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    if (!enabled || !src) {
      setDark(false);
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

        let total = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha < 16) continue; // Ignore transparent background pixels.
          total += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
          count += 1;
        }
        if (!cancelled && count > 0) setDark(total / count < DARK_LUMINANCE_THRESHOLD);
      } catch {
        // Tainted canvas (no CORS) or decode failure — leave `dark` false.
      }
    };
    img.onerror = () => {};
    img.src = src;

    return () => { cancelled = true; };
  }, [src, enabled]);

  return dark;
}
